import { useEffect, useState, type CSSProperties } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, StickerCard } from "../lib/components";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { fetchMatches, type MatchEntry } from "../api/matches";
import { fetchMyStats, type AlbumStats } from "../api/inventory";
import {
  fetchCommunitySummary,
  fetchTopCollectors,
  fetchByCountry,
  fetchRecentTrades,
  type CommunitySummary,
  type CollectorRow,
  type CountryRow,
  type RecentTradeRow,
} from "../api/community";
import type { Route } from "../App";

const DONATE_BANNER_KEY = "panini.donate_banner_dismissed_until";

function shouldShowDonateBanner(): boolean {
  const until = localStorage.getItem(DONATE_BANNER_KEY);
  if (!until) return true;
  return Date.now() > Number(until);
}

function dismissDonateBanner(days = 7): void {
  const until = Date.now() + days * 86400000;
  localStorage.setItem(DONATE_BANNER_KEY, String(until));
}

interface Props {
  go: (r: Route) => void;
}

export function Dashboard({ go }: Props) {
  const { user } = useAuth();
  const { stickerById, teamById } = useCatalog();
  const { ownership, loading: invLoading } = useInventory();

  const [stats, setStats] = useState<AlbumStats | null>(null);
  const [matches, setMatches] = useState<MatchEntry[]>([]);
  const [showBanner, setShowBanner] = useState(() => shouldShowDonateBanner());

  useEffect(() => {
    fetchMyStats().then(setStats).catch(() => null);
    fetchMatches(3).then(setMatches).catch(() => setMatches([]));
  }, [ownership]);

  const dismissBanner = () => {
    dismissDonateBanner(7);
    setShowBanner(false);
  };

  if (!user) return null;
  if (!stats) return <Loading />;

  const hasInventory = stats.owned + stats.dups > 0;

  return (
    <div className="page">
      {showBanner && (
        <div className="donate-banner" role="region" aria-label="Apoya el proyecto">
          <div className="donate-banner-icon">☕</div>
          <div className="donate-banner-text">
            <b>PaniniNilabs es 100% gratis</b> y lo mantenemos como un proyecto de Nilabs.
            Si te ayudó a completar tu álbum, una propinita ayuda a pagar el servidor 🙏
          </div>
          <div className="donate-banner-actions">
            <button className="btn btn-primary btn-sm" onClick={() => go("donate")}>
              Apoyar el proyecto
            </button>
            <button
              className="icon-btn"
              onClick={dismissBanner}
              aria-label="Ocultar por 7 días"
              title="Ocultar por 7 días"
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="hero">
        <div className="hero-content" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 32, alignItems: "center" }}>
          <div>
            <div className="chip" style={{ background: "rgba(255,255,255,0.18)", color: "white", border: 0, marginBottom: 12 }}>
              <Icon name="flame" size={12} /> {matches.length} matches activos
            </div>
            <h1>
              ¡Hola, {user.name.split(" ")[0]}! Te faltan <span style={{ color: "var(--accent-amber)" }}>{stats.missing}</span> láminas
            </h1>
            <p>{stats.dups > 0 ? `Tienes ${stats.dups} repetidas listas para intercambiar.` : "Marca tus láminas pegadas y repetidas en \"Mi álbum\" para empezar a hacer matches."}</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" onClick={() => go("album")} style={{ background: "white", color: "var(--pitch-700)" }}>
                {hasInventory ? "Editar álbum" : "Llenar mi álbum"} <Icon name="arrow" size={16} />
              </button>
              {stats.dups > 0 && (
                <button className="btn btn-ghost" onClick={() => go("dups")} style={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                  Ver repetidas
                </button>
              )}
            </div>
          </div>
          <div className="progress-ring" style={{ "--p": stats.pct, "--size": "140px" } as CSSProperties}>
            <div className="pct" style={{ fontSize: 32 }}>{stats.pct}%</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 24 }}>
        {[
          { label: "Pegadas", value: stats.owned, accent: "var(--primary)" },
          { label: "Faltantes", value: stats.missing, accent: "var(--accent-magenta)" },
          { label: "Repetidas", value: stats.dups, accent: "var(--accent-amber)" },
          { label: "Intercambios", value: user.trades_completed, accent: "var(--accent-cobalt)" },
        ].map((s) => (
          <div key={s.label} className="card" style={{ borderLeft: `3px solid ${s.accent}` }}>
            <div className="stat">
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginTop: 32 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontFamily: "var(--font-display)", margin: 0, fontSize: 22, letterSpacing: "-0.02em" }}>Top matches</h2>
            <div style={{ flex: 1 }} />
            {matches.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => go("matches")}>
                Ver todos <Icon name="arrow" size={14} />
              </button>
            )}
          </div>

          {matches.length === 0 ? (
            <EmptyMatches stats={stats} go={go} />
          ) : (
            <div className="col" style={{ gap: 12 }}>
              {matches.map(({ user: other, score, i_give_count, you_give_count, i_give_sticker_ids, you_give_sticker_ids }) => (
                <div key={other.id} className="match-row">
                  <div className="match-side">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar user={other} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{other.name}</div>
                        <div className="tiny muted">
                          {other.city || "—"} · <Icon name="star" size={10} /> {Number(other.stars_avg || 0).toFixed(1)}
                        </div>
                      </div>
                    </div>
                    <div className="match-stickers">
                      {you_give_sticker_ids.slice(0, 4).map((sid) => {
                        const s = stickerById(sid);
                        if (!s) return null;
                        const team = teamById(s.team_id);
                        return <StickerCard key={s.id} sticker={s} teamColor={team?.color} teamCode={team?.code} count={2} showDup={false} />;
                      })}
                    </div>
                    <div className="tiny muted">
                      <b>{other.name.split(" ")[0]}</b> te puede dar {you_give_count}
                    </div>
                  </div>
                  <div className="match-arrow">
                    <div className="match-score">{score}</div>
                    <div className="tiny bold">MATCH</div>
                  </div>
                  <div className="match-side" style={{ alignItems: "flex-end" }}>
                    <div className="match-stickers" style={{ justifyContent: "flex-end" }}>
                      {i_give_sticker_ids.slice(0, 4).map((sid) => {
                        const s = stickerById(sid);
                        if (!s) return null;
                        const team = teamById(s.team_id);
                        return <StickerCard key={s.id} sticker={s} teamColor={team?.color} teamCode={team?.code} count={2} showDup={false} />;
                      })}
                    </div>
                    <div className="tiny muted" style={{ textAlign: "right" }}>Tú le das {i_give_count}</div>
                    <button className="btn btn-primary btn-sm" onClick={() => go("matches")}>
                      Ver detalle
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Tu cuenta</div></div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar user={user} size="lg" />
              <div>
                <div style={{ fontWeight: 700 }}>{user.name}</div>
                <div className="tiny muted">{user.handle || user.email}</div>
                <div className="tiny muted" style={{ marginTop: 2 }}>{user.city || "Sin ciudad"}</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ background: "linear-gradient(135deg, var(--bg-card), var(--primary-soft))" }}>
            <div className="card-title" style={{ marginBottom: 8 }}>Tu nivel</div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--primary)", color: "white", display: "grid", placeItems: "center", fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800 }}>
                {user.level}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Coleccionista{user.level >= 10 ? " Estrella" : ""}</div>
                <div className="tiny muted">{user.xp} / {user.level * 100} XP</div>
                <div style={{ width: 140, height: 6, background: "var(--bg-subtle)", borderRadius: 3, marginTop: 6, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, (user.xp / (user.level * 100)) * 100)}%`, height: "100%", background: "var(--primary)" }} />
                </div>
              </div>
            </div>
          </div>

          {invLoading && <div className="tiny muted">Cargando inventario…</div>}
        </div>
      </div>

      <CommunitySection go={go} />
    </div>
  );
}

function EmptyMatches({ stats, go }: { stats: AlbumStats; go: (r: Route) => void }) {
  if (stats.dups === 0 && stats.owned === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32 }}>
        <Icon name="grid" size={32} />
        <h3 style={{ margin: "12px 0 6px", fontFamily: "var(--font-display)" }}>Empieza llenando tu álbum</h3>
        <p style={{ color: "var(--fg-muted)", fontSize: 14, margin: "0 0 16px" }}>
          Marca tus láminas pegadas y repetidas. Cuando otros usuarios se registren con láminas que necesitas, aparecerán acá automáticamente.
        </p>
        <button className="btn btn-primary" onClick={() => go("album")}>
          <Icon name="grid" size={14} /> Ir a mi álbum
        </button>
      </div>
    );
  }
  return (
    <div className="card" style={{ textAlign: "center", padding: 32 }}>
      <Icon name="sparkle" size={32} />
      <h3 style={{ margin: "12px 0 6px", fontFamily: "var(--font-display)" }}>Sin matches todavía</h3>
      <p style={{ color: "var(--fg-muted)", fontSize: 14, margin: 0 }}>
        Aún no hay otros coleccionistas con láminas que coincidan. Comparte PaniniNilabs con tus amigos para que se registren.
      </p>
    </div>
  );
}

function Loading() {
  return <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Cargando…</div>;
}

// ============================================================================
// CommunitySection — pulso de la comunidad para el dashboard.
// ============================================================================

function CommunitySection({ go }: { go: (r: Route) => void }) {
  const [summary, setSummary] = useState<CommunitySummary | null>(null);
  const [top, setTop] = useState<CollectorRow[]>([]);
  const [byCountry, setByCountry] = useState<CountryRow[]>([]);
  const [recent, setRecent] = useState<RecentTradeRow[]>([]);

  useEffect(() => {
    fetchCommunitySummary().then(setSummary).catch(() => null);
    fetchTopCollectors(5).then(setTop).catch(() => setTop([]));
    fetchByCountry().then(setByCountry).catch(() => setByCountry([]));
    fetchRecentTrades(5).then(setRecent).catch(() => setRecent([]));
  }, []);

  if (!summary) return null;

  return (
    <section className="community-section" style={{ marginTop: 40 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "var(--font-display)", margin: 0, fontSize: 22, letterSpacing: "-0.02em" }}>
          🌎 La comunidad
        </h2>
        <span className="tiny muted">El pulso de PaniniNilabs en tiempo real</span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={() => go("users")}>
          Ver coleccionistas <Icon name="arrow" size={14} />
        </button>
      </div>

      {/* 4 stats principales */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <CommunityStatCard
          label="Coleccionistas"
          value={summary.users_active}
          sub={summary.users_new_7d > 0 ? `+${summary.users_new_7d} esta semana` : "activos ahora"}
          accent="var(--primary)"
        />
        <CommunityStatCard
          label="% promedio del álbum"
          value={`${summary.avg_album_pct}%`}
          sub={`${summary.total_owned.toLocaleString("es-CO")} láminas pegadas en total`}
          accent="var(--accent-cobalt)"
        />
        <CommunityStatCard
          label="Trueques completados"
          value={summary.trades_completed}
          sub={summary.trades_last_7d > 0 ? `${summary.trades_last_7d} en los últimos 7 días` : "histórico total"}
          accent="var(--accent-magenta)"
        />
        <CommunityStatCard
          label="Repetidas circulando"
          value={summary.total_dups.toLocaleString("es-CO")}
          sub="copias para intercambiar"
          accent="var(--accent-amber)"
        />
      </div>

      {/* Grid de 2 columnas: Top coleccionistas + Por país */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 24 }}>
        {/* Top coleccionistas */}
        {top.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🏆</span>
              <b>Top 5 coleccionistas</b>
            </div>
            <div>
              {top.map((c, i) => (
                <div key={c.user.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: i === 0 ? 0 : "1px solid var(--border)" }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: i === 0 ? "var(--accent-amber)" : i === 1 ? "var(--fg-muted)" : i === 2 ? "#cd7f32" : "var(--bg-subtle)",
                    color: i < 3 ? "white" : "var(--fg-muted)",
                    display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, flexShrink: 0,
                  }}>
                    {i + 1}
                  </div>
                  <Avatar user={c.user} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.user.name}</div>
                    <div className="tiny muted">Nv. {c.user.level} · {c.user.country}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "var(--primary)" }}>{c.album_pct}%</div>
                    <div className="tiny muted">{c.owned} pegadas</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Por país */}
        {byCountry.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🌍</span>
              <b>Por país</b>
            </div>
            <div>
              {byCountry.slice(0, 5).map((c, i) => (
                <div key={c.country} style={{ padding: "10px 16px", borderTop: i === 0 ? 0 : "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <b style={{ fontSize: 13, flex: 1 }}>{c.country}</b>
                    <span className="tiny muted">{c.users_count} {c.users_count === 1 ? "user" : "users"}</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "var(--accent-cobalt)" }}>{c.avg_album_pct}%</span>
                  </div>
                  <div style={{ height: 6, background: "var(--bg-subtle)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${c.avg_album_pct}%`, height: "100%", background: "var(--accent-cobalt)", transition: "width 0.4s" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actividad reciente */}
      {recent.length > 0 && (
        <div>
          <h3 style={{ marginBottom: 12, fontSize: 16 }}>⚡ Actividad reciente</h3>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {recent.map((t, i) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: i === 0 ? 0 : "1px solid var(--border)" }}>
                <Avatar user={t.from_user} />
                <div className="tiny" style={{ flex: 1, minWidth: 0 }}>
                  <b>{t.from_user.name}</b> intercambió{" "}
                  <span className="chip primary" style={{ fontSize: 10, padding: "0 6px" }}>{t.items_count} {t.items_count === 1 ? "lámina" : "láminas"}</span>
                  {" "}con <b>{t.to_user.name}</b>
                </div>
                <span className="tiny muted" style={{ flexShrink: 0 }}>{relativeTimeAgo(t.completed_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function CommunityStatCard({ label, value, sub, accent }: { label: string; value: number | string; sub?: string; accent: string }) {
  return (
    <div className="card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="stat-label">{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, lineHeight: 1.1, marginTop: 4 }}>
        {typeof value === "number" ? value.toLocaleString("es-CO") : value}
      </div>
      {sub && <div className="tiny muted" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function relativeTimeAgo(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
